import React from 'react';
import PropTypes from 'prop-types';
import {useTranslation} from 'react-i18next';
import {
  Accordion,
  ListSelect,
  Loader,
  ProjectSharingSuggestions,
  SharePanel,
  SocialShareButtons
} from '@communityshare/ui-shared';
import {PayloadStates} from '@lore/utils';
import {useConnect} from '@lore/query-connect';
import DialogTemplate from '@/dialogs_common/DialogTemplate';
import CloseDialogButton from '@/dialogs_common/CloseDialogButton';
import EventLinkPreview from './EventLinkPreview';
import useEventShareActions from '@/features/share-event/hooks/useEventShareActions';

EventShareDialog.propTypes = {
  event: PropTypes.shape({
    id: PropTypes.number,
    state: PropTypes.string,
    data: PropTypes.shape({
      title: PropTypes.string,
      networkId: PropTypes.number
    })
  }).isRequired,
  onClose: PropTypes.func.isRequired,
  fromManage: PropTypes.bool
};

export default function EventShareDialog(props) {
  const {event, onClose, fromManage = false} = props;
  const {t} = useTranslation();
  const shareActions = useEventShareActions(event);
  const network = useConnect('network.byId', {
    id: event?.data?.networkId
  }, {enabled: !!event?.data?.networkId});

  if (event.state === PayloadStates.FETCHING || network.state === PayloadStates.FETCHING) {
    return (
      <DialogTemplate>
        <Loader/>
      </DialogTemplate>
    );
  }

  const title = event.data.title || 'INCOMPLETE';
  const brand = network?.data?.name || t('event_share.brand');

  return (
    <DialogTemplate className="w-screen max-w-screen-lg !bg-cs-gray-100">
      <CloseDialogButton onClick={onClose}/>
      <SharePanel
        breadcrumbParent={fromManage ? t('event_share.breadcrumb_parent') : null}
        breadcrumbAction={t('event_share.breadcrumb_action')}
        backLabel={t('event_share.back_to_event')}
        onBack={onClose}
        title={t('event_share.title')}
        subtitle={t('event_share.subtitle')}
        selectLabel={t('event_share.field_label')}
        select={
          <ListSelect
            value={event}
            options={[event.data]}
            disabled={true}
            getOptionLabel={() => event.data.title || 'INCOMPLETE'}
            getOptionKey={() => event.id}
            className="bg-white"
            onChange={() => {}}
          />
        }
        calloutBold={t('event_share.callout_bold')}
        calloutBody={t('event_share.callout_body')}
        suggestions={
          <Accordion>
            <ProjectSharingSuggestions
              options={[
                {
                  key: 'find_partners',
                  label: t('event_share.option_find_partners'),
                  template: t('event_share.template_find_partners', {title})
                },
                {
                  key: 'spread_word',
                  label: t('event_share.option_spread_word', {brand}),
                  template: t('event_share.template_spread_word', {brand})
                }
              ]}
              examples={[
                {id: 'example-1', text: t('event_share.example_1')},
                {id: 'example-2', text: t('event_share.example_2')},
              ]}
              helpTitle={t('event_share.help_title')}
              viewSuggestionsLabel={t('event_share.view_suggestions')}
              helpQuestion={t('event_share.help_question')}
              templateLabel={t('event_share.template_label')}
              resetTemplateLabel={t('event_share.reset_template')}
              copyLabel={t('event_share.copy_text')}
              copiedLabel={t('event_share.copied')}
              seeExamplesLabel={t('event_share.see_examples')}
            />
          </Accordion>
        }
        shareActions={
          <SocialShareButtons
            {...shareActions}
            copiedLinkLabel={t('controls.Copied')}
            copyLinkLabel={t('controls.Copy link')}
          />
        }
      >
        {/* key remounts the preview on event change so event-specific
            state (selected image, default-save guard) resets. */}
        <EventLinkPreview key={event.id} event={event}/>
      </SharePanel>
    </DialogTemplate>
  );
}
